import { lazy, ReactElement } from 'react';
import type { AppProps } from '@paypalcorp/cms-engine';
import type { EmotionCache } from '@emotion/cache';
import { EmotionCMSPage } from '@paypalcorp/cms-engine/components';
import './styles-overrides.css';
import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme';
import { NavFragment } from '@paypalcorp/globalnavsdk/NavFragment';
import { ParentProvider } from '@paypalcorp/pp-com-components/components/ParentProvider'; 
import { DocumentBody } from '@paypalcorp/pp-com-components/components/DocumentBody';
import { Placement } from '@paypalcorp/pp-com-components/components/Placement';
import { QuantumLeapFooter } from '../components/QuantumLeapFooter';


interface CustomAppProps {
  emotionCache: EmotionCache;
  renderContext: AppProps;
}

export default function App({
  emotionCache, renderContext,
}: Readonly<CustomAppProps>) {
  const {
    componentKit, pageContext, pageModel, ...props
  } = renderContext || {};
  const { skipHeader, skipFooter } = pageContext?.additionalContext || {};
  // Section transitions must only run when ECM explicitly enables them at the
  // page level; without this they default on and break pages using components
  // (e.g. "featured" StackSection) that enable transitions by default.
  const enableSectionTransitions = pageContext?.cmsEngineContext?.environment?.enableSectionTransitions ?? false;
  const ComponentsMap: Record<string, ReactElement | any> = {};
  const bodyClasses = ['document-body'];
  try {
    Object.keys(componentKit).forEach((key) => {
      ComponentsMap[key] = componentKit[key]?.bundle || lazy(() => import(`../../../node_modules/@paypalcorp/pp-com-components/dist/components/${key}.js`));
    });
  } catch (error) {
    console.error('Error loading components: ', error);
  }

  return (
    <EmotionCMSPage
      data-testid="cms-page"
      componentKit={{
        Parent: ParentProvider,
        Placement,
        ...ComponentsMap,
      }}
      theme={PAYPAL_THEME}
      hostedBy='venmocmsnodeweb'
      pageContext={pageContext}
      pageModel={pageModel}
      {...props}
      emotionCache={emotionCache}
      render={({
        aboveHeader, body, belowFooter,
      })  => (
        <> 
          {aboveHeader}
          {!skipHeader && <NavFragment type="header" />}
          <DocumentBody
            className={bodyClasses.join(' ')}
            data-testid="document-body-element"
            disableSectionTransitions={!enableSectionTransitions}
          >
            {body}
          </DocumentBody>
          <QuantumLeapFooter belowFooter={belowFooter} skipFooter={skipFooter} />
        </>
      )}
    />
  );
}
